import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const scope = [
  {
    icon: "ri-film-line",
    title: "影视作品资料",
    desc: "整理作品名称、类型、上映年份、导演、主演与剧情梗概，并按类型与年份提供筛选。",
  },
  {
    icon: "ri-newspaper-line",
    title: "动态资讯报道",
    desc: "收录作品上映、预告发布、评选提名等影视动态，帮助影迷及时掌握行业信息。",
  },
  {
    icon: "ri-user-star-line",
    title: "角色解析内容",
    desc: "聚焦角色本身，提供背景设定、代表台词与关键剧情片段，理解角色与作品的关联。",
  },
];

export default function About() {
  useSeo({
    title: "关于本站 - 希岛爱理影视内容站",
    description:
      "了解希岛爱理影视内容站的定位、内容范围与版权说明。本站为影迷内容整理平台，提供影视作品、资讯与角色解析资料。",
    keywords: "关于本站,希岛爱理影视站,内容范围,版权声明",
  });

  return (
    <div className="w-full pb-16">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />

      <div className="w-full px-4 md:px-8 pt-6">
        <div className="rounded-lg bg-primary-950 text-background-100 p-6 md:p-12">
          <span className="text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-400">
            About
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mt-2">
            关于本站
          </h1>
          <p className="text-sm md:text-base text-background-300 mt-4 max-w-3xl leading-loose">
            本站是一个围绕日本演员希岛爱理的内容整理平台，专注于影视作品、动态资讯与角色解析三类资料。我们希望用清晰的结构与克制的呈现，让影迷能够快速找到想了解的内容，而不被冗余信息干扰。
          </p>
        </div>
      </div>

      <section className="w-full px-4 md:px-8 pt-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
          内容范围
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {scope.map((item) => (
            <div
              key={item.title}
              className="rounded-lg bg-background-100 border border-background-200 p-6"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900 mb-4">
                <i className={`${item.icon} text-xl`} />
              </div>
              <h3 className="font-heading font-semibold text-foreground-950">{item.title}</h3>
              <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">联系我们</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              如果你的资料内容有误，或希望补充新的作品与资讯信息，欢迎通过邮箱与我们联系。我们会在核对后及时更新。
            </p>
            <div className="mt-5 flex items-center gap-3 rounded-md bg-background-50 border border-background-200 px-4 py-3 w-fit">
              <i className="ri-mail-line text-accent-600 text-lg" />
              <a
                href="mailto:contact@kirishima-airi.fans"
                className="text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors"
              >
                contact@kirishima-airi.fans
              </a>
            </div>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">版权声明</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              本站为非官方影迷内容整理平台，所有作品与角色相关信息仅用于内容展示与学习交流。相关影视作品、剧照及人物形象的版权归原权利方所有。如涉及版权问题，请通过上方邮箱与我们联系，我们将及时处理。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mt-3">
              当前页面内容为演示数据，用于展示站点的浏览、筛选与搜索体验。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-12">
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">开始浏览</h2>
            <p className="text-sm text-foreground-600 mt-1">从作品列表、资讯或角色专题任选一个入口。</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/works"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-film-line" />
              影视作品
            </Link>
            <Link
              to="/characters"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-primary-950 text-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-star-line" />
              角色解析
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}